import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import axios from 'axios';
import { layoutDepartmentColumns } from '../orgChartLayout';

const API = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: 'Bearer ' + token() });
const NAVY = '#1A3A5C';
const ORANGE = '#EA6F2E';
// Clean-design palette (matches the HR Overview org-chart panel).
const DEPT_COLOR = { 'COLLECTIONS': '#2f8f6b', 'PUBLIC ENGAGEMENT': '#7a4fa5', 'ADMINISTRATION': '#3b5ba5', 'EXHIBITIONS': '#a5533b' };
const titleCase = (s) => (s ? s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : s);

function Toast({ message, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, []);
  return (
    <div style={{ position:'fixed', bottom:'32px', left:'50%', transform:'translateX(-50%)', zIndex:9999, background:'#111827', color:'#fff', padding:'13px 24px', borderRadius:'10px', boxShadow:'0 8px 32px rgba(0,0,0,0.3)', fontSize:'13px', fontWeight:'500' }}>
      {message}
    </div>
  );
}

function EditPositionModal({ position, onClose, onSave, availableStaff, allStaff = [] }) {
  const isAdd = !position;
  const supervisors = allStaff.filter(s => s.is_supervisor);

  const [form, setForm] = useState({
    position_title: position?.position_title || '',
    position_number: position?.position_number || '',
    department: position?.department || '',
    sub_department: position?.sub_department || '',
    staff_id: position?.staff_id || null,
    is_temp: position?.is_temp || false,
    is_not_in_py_count: position?.is_not_in_py_count || false,
    intended_supervisor_id: position?.intended_supervisor_id || null
  });
  // "Other" reveals a full-staff picker. Start in Other mode only if an already-set
  // supervisor isn't in the designated list (defensive; Add always starts clean).
  const [otherMode, setOtherMode] = useState(
    !!position?.intended_supervisor_id && !supervisors.some(s => s.id === position.intended_supervisor_id)
  );
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({...f, [k]: v}));

  const labelStyle = { display:'block', fontSize:'11px', fontWeight:'600', color:'#6B7280', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.05em' };
  const baseInput = { width:'100%', padding:'11px 12px', border:'1.5px solid #E5E7EB', borderRadius:'10px', fontSize:'14px', outline:'none', color:'#111827', background:'#fff', boxSizing:'border-box', transition:'border-color 0.15s, box-shadow 0.15s' };
  const onFocus = e => { e.target.style.borderColor = NAVY; e.target.style.boxShadow = '0 0 0 3px rgba(26,58,92,0.10)'; };
  const onBlur  = e => { e.target.style.borderColor = '#E5E7EB'; e.target.style.boxShadow = 'none'; };
  const fieldProps = { style: baseInput, onFocus, onBlur };

  const handleSave = async () => {
    if (!form.position_title.trim()) { alert('Position title is required'); return; }
    if (!form.department) { alert('Please choose a department — it determines where the card appears in the org chart.'); return; }
    setSaving(true);
    try {
      await onSave(position?.id, form);
      onClose();
    } catch (e) {
      alert('Failed to save: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(17,24,39,0.5)', zIndex:1000, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'48px 16px', backdropFilter:'blur(2px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:'16px', width:'520px', maxWidth:'100%', maxHeight:'86vh', overflowY:'auto', boxShadow:'0 24px 64px rgba(0,0,0,0.28)' }}>
        {/* Header */}
        <div style={{ padding:'22px 26px 16px', borderBottom:'1px solid #F1F1F3', position:'sticky', top:0, background:'#fff', borderTopLeftRadius:'16px', borderTopRightRadius:'16px', zIndex:1 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <h2 style={{ fontSize:'19px', fontWeight:700, color:'#111827', margin:'0 0 3px', letterSpacing:'-0.01em' }}>
                {isAdd ? 'Add Position' : 'Edit Position'}
              </h2>
              <p style={{ fontSize:'12.5px', color:'#8A8A8E', margin:0 }}>
                {isAdd ? 'Create an opening and set who will supervise it.' : 'Update details or place the selected candidate.'}
              </p>
            </div>
            <button onClick={onClose} style={{ background:'#F5F5F7', border:'none', width:'30px', height:'30px', borderRadius:'8px', cursor:'pointer', color:'#6B7280', fontSize:'18px', lineHeight:'30px', flexShrink:0 }}>×</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding:'22px 26px', display:'grid', gap:'18px' }}>
          <div>
            <label style={labelStyle}>Position Title *</label>
            <input type="text" value={form.position_title} onChange={e => set('position_title', e.target.value)} placeholder="e.g. Museum Curator I" {...fieldProps} />
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
            <div>
              <label style={labelStyle}>Position Number</label>
              <input type="text" value={form.position_number} onChange={e => set('position_number', e.target.value)} placeholder="Optional" {...fieldProps} />
            </div>
            <div>
              <label style={labelStyle}>Department *</label>
              <select value={form.department} onChange={e => set('department', e.target.value)} {...fieldProps}>
                <option value="">Select…</option>
                <option value="EXECUTIVE">Executive</option>
                <option value="COLLECTIONS">Collections</option>
                <option value="PUBLIC ENGAGEMENT">Public Engagement</option>
                <option value="ADMINISTRATION">Administration</option>
                <option value="EXHIBITIONS">Exhibitions</option>
              </select>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Sub-Department</label>
            <input type="text" value={form.sub_department} onChange={e => set('sub_department', e.target.value)} placeholder="Optional" {...fieldProps} />
          </div>

          {/* ADD: who will supervise the opening (designated supervisors + Other → any staff) */}
          {isAdd && (
            <div>
              <label style={labelStyle}>Will be supervised by</label>
              <select
                value={otherMode ? '__other__' : (form.intended_supervisor_id || '')}
                onChange={e => {
                  const v = e.target.value;
                  if (v === '__other__') { setOtherMode(true); set('intended_supervisor_id', null); }
                  else { setOtherMode(false); set('intended_supervisor_id', v ? parseInt(v) : null); }
                }}
                {...fieldProps}
              >
                <option value="">Select supervisor…</option>
                {supervisors.map(s => (
                  <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                ))}
                <option value="__other__">Other (choose any staff)…</option>
              </select>
              {otherMode && (
                <select
                  value={form.intended_supervisor_id || ''}
                  onChange={e => set('intended_supervisor_id', e.target.value ? parseInt(e.target.value) : null)}
                  {...fieldProps}
                  style={{ ...baseInput, marginTop:'10px' }}
                >
                  <option value="">Select staff member…</option>
                  {allStaff.map(s => (
                    <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                  ))}
                </select>
              )}
              <div style={{ fontSize:'11.5px', color:'#9CA3AF', marginTop:'6px' }}>
                They'll be notified (in-app + email) that they'll oversee whoever fills this opening.
              </div>
            </div>
          )}

          {/* EDIT: place the winning candidate into this position */}
          {!isAdd && (
            <div>
              <label style={labelStyle}>Assign Staff Member</label>
              <select value={form.staff_id || ''} onChange={e => set('staff_id', e.target.value ? parseInt(e.target.value) : null)} {...fieldProps}>
                <option value="">Vacant</option>
                {availableStaff.map(s => (
                  <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                ))}
                {position?.staff_id && (
                  <option value={position.staff_id}>{position.first_name} {position.last_name} (current)</option>
                )}
              </select>
            </div>
          )}

          <div style={{ display:'flex', gap:'20px', paddingTop:'2px' }}>
            <label style={{ display:'flex', alignItems:'center', gap:'8px', cursor:'pointer' }}>
              <input type="checkbox" checked={form.is_temp} onChange={e => set('is_temp', e.target.checked)} style={{ width:'16px', height:'16px', accentColor: ORANGE, cursor:'pointer' }} />
              <span style={{ fontSize:'13px', color:'#374151' }}>Temporary / Intermittent</span>
            </label>
            <label style={{ display:'flex', alignItems:'center', gap:'8px', cursor:'pointer' }}>
              <input type="checkbox" checked={form.is_not_in_py_count} onChange={e => set('is_not_in_py_count', e.target.checked)} style={{ width:'16px', height:'16px', accentColor: ORANGE, cursor:'pointer' }} />
              <span style={{ fontSize:'13px', color:'#374151' }}>Not in PY Count</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', padding:'16px 26px 22px', borderTop:'1px solid #F1F1F3', position:'sticky', bottom:0, background:'#fff' }}>
          <button onClick={onClose} style={{ padding:'10px 18px', border:'1.5px solid #E5E7EB', background:'#fff', borderRadius:'10px', fontSize:'13px', fontWeight:600, color:'#374151', cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding:'10px 22px', border:'none', background: ORANGE, color:'#fff', borderRadius:'10px', fontSize:'13px', fontWeight:700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : (isAdd ? 'Create Position' : 'Save Changes')}
          </button>
        </div>
      </div>
    </div>
  );
}

function PositionCard({ position, onEdit, onDelete, currentUser, supervisorName }) {
  const isAdmin = currentUser?.role === 'admin';
  const vacant = position.status === 'vacant';
  // Awarded-but-not-yet-provisioned: card shows the incoming candidate's identity.
  const incoming = !position.staff_id && (position.status === 'incoming' || !!position.incoming_first_name);
  const temp = position.is_temp;
  const notPy = position.is_not_in_py_count;
  // Precedence: Temp-Intermittent (orange) → Not Incl. PY Count (grey) → Incoming (blue)
  // → Vacancy (green) → Staff Member (white).
  const bg = temp ? '#fff3e6' : notPy ? '#f3f4f6' : incoming ? '#e9f0fb' : vacant ? '#d4f3df' : '#fff';
  const border = temp ? '#f0c48a' : notPy ? '#d1d5db' : incoming ? '#9db9e8' : vacant ? '#5cb97f' : '#d7dee8';
  const displayName = incoming
    ? `${position.incoming_first_name || ''} ${position.incoming_last_name || ''}`.trim()
    : position.staff_id ? `${position.first_name} ${position.last_name}` : 'Vacant';
  // Timezone-safe short date from a YYYY-MM-DD(THH..) start date.
  const startStr = (() => {
    if (!position.incoming_start_date) return null;
    const p = String(position.incoming_start_date).slice(0, 10).split('-');
    if (p.length !== 3) return null;
    const mo = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][(+p[1]) - 1];
    return `${mo} ${+p[2]}, ${p[0]}`;
  })();
  const sub = position.sub_department ? ` · ${titleCase(position.sub_department)}` : '';

  return (
    <div style={{
      position:'relative', width:'100%', maxWidth:'230px',
      background:bg, border:`1px solid ${border}`, borderRadius:'10px',
      padding:'8px 12px', boxShadow:'0 1px 1px rgba(16,24,40,.05)',
      marginBottom:'10px', transition:'all 0.15s'
    }}>
      {isAdmin && (
        <div style={{ position:'absolute', top:'6px', right:'6px', display:'flex', gap:'4px', zIndex:10 }}>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(position); }}
            style={{ width:'22px', height:'22px', borderRadius:'6px', border:'none', background:'#FEF3C7', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
            title="Edit"
          >
            <svg width="11" height="11" fill="none" stroke="#92400E" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(position); }}
            style={{ width:'22px', height:'22px', borderRadius:'6px', border:'none', background:'#FEE2E2', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
            title="Delete"
          >
            <svg width="11" height="11" fill="none" stroke="#991B1B" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
            </svg>
          </button>
        </div>
      )}

      <div style={{ fontSize:'12px', fontWeight:700, lineHeight:1.2, color: incoming ? '#1d4ed8' : vacant ? '#166534' : NAVY, paddingRight: isAdmin ? '50px' : 0 }}>{displayName}</div>
      <div style={{ fontSize:'10px', color:'#6b7280', marginTop:'2px', lineHeight:1.25 }}>{position.position_title}{sub}</div>
      {position.position_number && (
        <div style={{ fontSize:'8.5px', color:'#9aa4b2', marginTop:'3px' }}>{position.position_number}</div>
      )}
      {incoming && (
        <div style={{ display:'inline-block', marginTop:'4px', fontSize:'8px', fontWeight:700, color:'#1d4ed8', background:'#dbe6fb', borderRadius:'5px', padding:'1px 6px', letterSpacing:'0.03em', textTransform:'uppercase' }}>
          Incoming{startStr ? ` · starts ${startStr}` : ''}
        </div>
      )}
      {!vacant && !incoming && supervisorName && (
        <div style={{ fontSize:'9px', color:'#6b7280', marginTop:'3px' }}>Supervisor: {supervisorName}</div>
      )}
      {(vacant || incoming) && (position.intended_supervisor_first || position.intended_supervisor_last) && (
        <div style={{ fontSize:'8.5px', color: incoming ? '#1d4ed8' : '#166534', marginTop:'3px', fontStyle:'italic' }}>
          Supervised by: {position.intended_supervisor_first || ''} {position.intended_supervisor_last || ''}
        </div>
      )}
    </div>
  );
}

export default function OrgChart({ currentUser }) {
  const [positions, setPositions] = useState([]);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPosition, setEditingPosition] = useState(null);
  const [addingPosition, setAddingPosition] = useState(false);
  const [toast, setToast] = useState(null);
  const [showTemps, setShowTemps] = useState(false); // "Staff Only" (default) vs "With Temps"

  // ----- Fit-to-screen scaling -----
  // Measure the chart's natural width vs. the available width and scale the whole
  // stage uniformly so it lands on one screen wherever the viewport allows; drops to
  // horizontal scroll at 100%+ on screens too narrow to fit.
  const viewportRef = useRef(null);
  const canvasRef = useRef(null);
  const [availW, setAvailW] = useState(0);
  const [nat, setNat] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState('fit'); // 'fit' | number (explicit scale)

  useLayoutEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const measure = () => {
      const cv = canvasRef.current;
      setAvailW(w => (w !== vp.clientWidth ? vp.clientWidth : w));
      if (cv) setNat(p => (p.w !== cv.scrollWidth || p.h !== cv.scrollHeight) ? { w: cv.scrollWidth, h: cv.scrollHeight } : p);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(vp);
    if (canvasRef.current) ro.observe(canvasRef.current);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [loading]);

  // Re-measure when the chart content changes (data load, temps toggle).
  useLayoutEffect(() => {
    const cv = canvasRef.current;
    if (cv) setNat(p => (p.w !== cv.scrollWidth || p.h !== cv.scrollHeight) ? { w: cv.scrollWidth, h: cv.scrollHeight } : p);
  }, [positions, showTemps, loading]);

  const fitScale = (availW && nat.w) ? Math.min(1, availW / nat.w) : 1;
  const scale = zoom === 'fit' ? fitScale : zoom;
  const pct = Math.round(scale * 100);
  const clampZoom = (z) => Math.max(0.3, Math.min(2, Math.round(z * 100) / 100));
  const nudgeZoom = (d) => setZoom(z => clampZoom((z === 'fit' ? fitScale : z) + d));
  const zoomStepStyle = { width:'26px', height:'26px', border:'none', background:'transparent', cursor:'pointer', fontSize:'16px', fontWeight:700, color:'#374151', borderRadius:'7px' };
  const zoomPill = (active) => ({ padding:'7px 14px', border:'none', borderRadius:'9px', fontSize:'12.5px', fontWeight:600, cursor:'pointer', background: active ? NAVY : '#F3F4F6', color: active ? '#fff' : '#374151', transition:'all 0.15s' });

  useEffect(() => {
    fetchPositions();
    fetchAvailableStaff();
    fetchAllStaff();
  }, []);

  // All staff — the modal derives designated supervisors (is_supervisor) for the
  // primary picker and uses the full list for the "Other" picker.
  const fetchAllStaff = async () => {
    try {
      const res = await axios.get(API + '/staff', { headers: headers() });
      setAllStaff(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchPositions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API + '/org-chart', { headers: headers() });
      setPositions(res.data);
    } catch (e) {
      console.error(e);
      setToast('Failed to load org chart');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableStaff = async () => {
    try {
      const res = await axios.get(API + '/org-chart/available-staff', { headers: headers() });
      setAvailableStaff(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSavePosition = async (positionId, data) => {
    if (positionId) {
      await axios.put(API + '/org-chart/' + positionId, data, { headers: headers() });
      setToast('Position updated!');
    } else {
      await axios.post(API + '/org-chart', data, { headers: headers() });
      setToast('Position created!');
    }
    fetchPositions();
    fetchAvailableStaff();
  };

  const handleDeletePosition = async (position) => {
    if (!window.confirm(`Are you sure you want to delete the ${position.position_title} position?`)) return;
    try {
      await axios.delete(API + '/org-chart/' + position.id, { headers: headers() });
      fetchPositions();
      setToast('Position deleted');
    } catch (e) {
      alert('Failed to delete: ' + (e.response?.data?.error || e.message));
    }
  };

  // "Staff Only" hides Temp-Intermittent positions; "With Temps" shows everyone.
  const visible = positions.filter(p => showTemps || !p.is_temp);
  const executive = visible.filter(p => p.department === 'EXECUTIVE');
  const collections = visible.filter(p => p.department === 'COLLECTIONS');
  const publicEngagement = visible.filter(p => p.department === 'PUBLIC ENGAGEMENT');
  const administration = visible.filter(p => p.department === 'ADMINISTRATION');
  const exhibitions = visible.filter(p => p.department === 'EXHIBITIONS');

  // Executive level: top role (e.g. Executive Director) sits on top; any peers at the
  // next level (Deputy Director + a peer vacancy) lay out side-by-side beneath it.
  const execSorted = [...executive].sort((a, b) => (a.hierarchy_level - b.hierarchy_level) || (a.display_order - b.display_order));
  const topLevel = execSorted.length ? execSorted[0].hierarchy_level : 0;
  const execTop = execSorted.filter(p => p.hierarchy_level === topLevel);
  const execRest = execSorted.filter(p => p.hierarchy_level !== topLevel);
  // Primary (e.g. Jon) stays centered under the top role; peers/vacancies sit to its right.
  const execPrimary = execRest[0] || null;
  const execExtras = execRest.slice(1);
  const supName = (p) => (p.supervisor_first || p.supervisor_last) ? `${p.supervisor_first || ''} ${p.supervisor_last || ''}`.trim() : null;

  if (loading) return <div style={{ padding:'40px', textAlign:'center', color:'#9CA3AF' }}>Loading org chart...</div>;

  return (
    <div style={{ padding:'24px 28px', overflowX:'auto' }}>
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
      {(editingPosition || addingPosition) && (
        <EditPositionModal 
          position={editingPosition} 
          onClose={() => { setEditingPosition(null); setAddingPosition(false); }}
          onSave={handleSavePosition}
          availableStaff={availableStaff}
          allStaff={allStaff}
        />
      )}

      <div style={{ background:'#fff', padding:'24px 28px', borderRadius:'14px', marginBottom:'24px', boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <div>
            <h2 style={{ fontSize:'22px', fontWeight:'600', color: NAVY, margin:0, marginBottom:'4px' }}>
              CALIFORNIA AFRICAN AMERICAN MUSEUM
            </h2>
            <h3 style={{ fontSize:'16px', fontWeight:'600', color: NAVY, margin:0, marginBottom:'4px' }}>
              ORGANIZATIONAL CHART
            </h3>
            <p style={{ fontSize:'12px', color:'#6B7280', margin:0 }}>
              as of {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} · FY 2024/25
            </p>
          </div>
          {currentUser?.role === 'admin' && (
            <button
              onClick={() => setAddingPosition(true)}
              style={{
                display:'flex', alignItems:'center', gap:'6px', padding:'10px 18px',
                background: ORANGE, color:'#fff', border:'none', borderRadius:'8px',
                fontSize:'14px', fontWeight:'600', cursor:'pointer', transition:'all 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#D85F1E'; }}
              onMouseLeave={e => { e.currentTarget.style.background = ORANGE; }}
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
              </svg>
              Add Position
            </button>
          )}
        </div>

        {/* Staff Only | With Temps toggle */}
        <div style={{ display:'flex', justifyContent:'center', marginTop:'6px' }}>
          <div style={{ display:'inline-flex', background:'#F3F4F6', borderRadius:'10px', padding:'3px', gap:'2px' }}>
            {[['staff','Staff Only'], ['temps','With Temps']].map(([key, label]) => {
              const active = (key === 'temps') === showTemps;
              return (
                <button
                  key={key}
                  onClick={() => setShowTemps(key === 'temps')}
                  style={{
                    padding:'7px 16px', border:'none', borderRadius:'8px', fontSize:'12.5px', fontWeight:600,
                    cursor:'pointer', background: active ? '#fff' : 'transparent',
                    color: active ? NAVY : '#6B7280', boxShadow: active ? '0 1px 2px rgba(16,24,40,0.10)' : 'none',
                    transition:'all 0.15s'
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Zoom / fit-to-screen controls */}
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:'10px', marginTop:'12px' }}>
          <button onClick={() => setZoom('fit')} style={zoomPill(zoom === 'fit')}>Fit to screen</button>
          <div style={{ display:'inline-flex', alignItems:'center', background:'#F3F4F6', borderRadius:'10px', padding:'3px' }}>
            <button onClick={() => nudgeZoom(-0.1)} style={zoomStepStyle} title="Zoom out">−</button>
            <span style={{ minWidth:'46px', textAlign:'center', fontSize:'12.5px', fontWeight:700, color:NAVY }}>{pct}%</span>
            <button onClick={() => nudgeZoom(0.1)} style={zoomStepStyle} title="Zoom in">+</button>
          </div>
          <button onClick={() => setZoom(1)} style={zoomPill(zoom === 1)}>100%</button>
        </div>

        <div style={{ marginTop:'28px', display:'flex', flexDirection:'column', gap:'24px', alignItems:'center' }}>
          {/* Scale stage: viewport measures available width; sizer reserves the scaled
              box; canvas holds the chart at natural size and is transform-scaled. */}
          <div ref={viewportRef} style={{ width:'100%', overflowX: zoom === 'fit' ? 'hidden' : 'auto', textAlign:'center' }}>
          <div style={{ display:'inline-block', verticalAlign:'top', position:'relative', width: nat.w ? nat.w * scale : 'auto', height: nat.h ? nat.h * scale : 'auto' }}>
          <div ref={canvasRef} style={{ position: nat.w ? 'absolute' : 'static', top:0, left:0, transform:`scale(${scale})`, transformOrigin:'top left', textAlign:'left', display:'flex', flexDirection:'column', alignItems:'center', gap:'0', width:'max-content' }}>
          {/* Executive Level */}
          {executive.length > 0 && (
            <div style={{ color:'#fff', fontWeight:700, fontSize:'10.5px', letterSpacing:'0.06em', textTransform:'uppercase', padding:'5px 16px', borderRadius:'8px', background: ORANGE, marginBottom:'10px' }}>
              Executive
            </div>
          )}
          {execTop.map(pos => (
            <PositionCard
              key={pos.id}
              position={pos}
              supervisorName={(pos.supervisor_first || pos.supervisor_last) ? `${pos.supervisor_first || ''} ${pos.supervisor_last || ''}`.trim() : null}
              onEdit={setEditingPosition}
              onDelete={handleDeletePosition}
              currentUser={currentUser}
            />
          ))}
          {execPrimary && (
            <>
              <div style={{ width:'2px', height:'16px', background:'#c9d2df' }} />
              <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr', alignItems:'start', columnGap:'16px', width:'100%' }}>
                <div />
                <div style={{ display:'flex', justifyContent:'center' }}>
                  <div style={{ width:'230px', flex:'0 0 auto' }}>
                    <PositionCard
                      position={execPrimary}
                      supervisorName={supName(execPrimary)}
                      onEdit={setEditingPosition}
                      onDelete={handleDeletePosition}
                      currentUser={currentUser}
                    />
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'flex-start', gap:'16px', flexWrap:'wrap' }}>
                  {execExtras.map(pos => (
                    <div key={pos.id} style={{ width:'230px', flex:'0 0 auto' }}>
                      <PositionCard
                        position={pos}
                        supervisorName={supName(pos)}
                        onEdit={setEditingPosition}
                        onDelete={handleDeletePosition}
                        currentUser={currentUser}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Connector bus: Executive → department columns */}
          {executive.length > 0 && (
            <>
              <div style={{ width:'2px', height:'16px', background:'#c9d2df' }} />
              <div style={{ height:'2px', width:'80%', background:'#c9d2df' }} />
              <div style={{ display:'flex', justifyContent:'space-around', width:'80%', marginBottom:'14px' }}>
                <div style={{ width:'2px', height:'14px', background:'#c9d2df' }} />
                <div style={{ width:'2px', height:'14px', background:'#c9d2df' }} />
                <div style={{ width:'2px', height:'14px', background:'#c9d2df' }} />
                <div style={{ width:'2px', height:'14px', background:'#c9d2df' }} />
              </div>
            </>
          )}

          {/* Department Columns — sub-department heads side-by-side, reports below */}
            <div style={{ display:'flex', gap:'22px', alignItems:'flex-start', width:'max-content' }}>
              {[['COLLECTIONS', collections], ['PUBLIC ENGAGEMENT', publicEngagement], ['ADMINISTRATION', administration], ['EXHIBITIONS', exhibitions]].map(([label, deptPositions]) => {
                const cols = layoutDepartmentColumns(deptPositions);
                const card = ({ pos, supervisorName }) => (
                  <div key={pos.id} style={{ width:'230px', flex:'0 0 auto' }}>
                    <PositionCard
                      position={pos}
                      supervisorName={supervisorName}
                      onEdit={setEditingPosition}
                      onDelete={handleDeletePosition}
                      currentUser={currentUser}
                    />
                  </div>
                );
                return (
                  <div key={label} style={{ flex:'0 0 auto' }}>
                    <div style={{ background: DEPT_COLOR[label], color:'#fff', padding:'7px', textAlign:'center', fontWeight:700, fontSize:'10.5px', letterSpacing:'0.05em', marginBottom:'14px', borderRadius:'8px' }}>
                      {label}
                    </div>
                    <div style={{ display:'flex', gap:'16px', justifyContent:'center', alignItems:'flex-start' }}>
                      {cols.map((col, i) => (
                        <div key={col.lead ? col.lead.pos.id : 'x' + i} style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
                          {col.lead && card(col.lead)}
                          {col.lead && col.reports.length > 0 && <div style={{ width:'2px', height:'16px', background:'#c9d2df' }} />}
                          <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
                            {col.reports.map(card)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>{/* canvas */}
          </div>{/* sizer */}
          </div>{/* viewport: scale stage */}

          {/* Legend / ledger */}
          <div style={{ marginTop:'36px', display:'flex', gap:'22px', justifyContent:'center', flexWrap:'wrap', fontSize:'11px', color:'#6b7280' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'7px' }}>
              <div style={{ width:'12px', height:'12px', borderRadius:'3px', background:'#fff', border:'1px solid #d7dee8' }}></div>
              <span>Staff Member</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'7px' }}>
              <div style={{ width:'12px', height:'12px', borderRadius:'3px', background:'#d4f3df', border:'1px solid #5cb97f' }}></div>
              <span>Vacancy</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'7px' }}>
              <div style={{ width:'12px', height:'12px', borderRadius:'3px', background:'#e9f0fb', border:'1px solid #9db9e8' }}></div>
              <span>Incoming (awarded)</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'7px' }}>
              <div style={{ width:'12px', height:'12px', borderRadius:'3px', background:'#f3f4f6', border:'1px solid #d1d5db' }}></div>
              <span>Not Incl. PY Count</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'7px' }}>
              <div style={{ width:'12px', height:'12px', borderRadius:'3px', background:'#fff3e6', border:'1px solid #f0c48a' }}></div>
              <span>Temp-Intermittent</span>
            </div>
          </div>

          {/* Empty state */}
          {positions.length === 0 && (
            <div style={{ textAlign:'center', padding:'60px 20px', color:'#9CA3AF' }}>
              <p style={{ fontSize:'14px', fontWeight:'600', color:'#6B7280', marginBottom:'12px' }}>
                No positions in org chart yet
              </p>
              {currentUser?.role === 'admin' && (
                <button
                  onClick={() => setAddingPosition(true)}
                  style={{ padding:'10px 20px', background: ORANGE, color:'#fff', border:'none', borderRadius:'8px', fontSize:'14px', fontWeight:'600', cursor:'pointer' }}
                >
                  Add First Position
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Summary Info */}
      <div style={{ marginTop:'24px', fontSize:'11px', color:'#6B7280', textAlign:'center' }}>
        <p>Total Positions: {positions.length} | Filled: {positions.filter(p => p.status === 'filled').length} | Vacant: {positions.filter(p => p.status === 'vacant').length}</p>
      </div>
    </div>
  );
}
